<template>
  <!-- display:contents — the .field children join the parent's .form-grid directly -->
  <div class="taf">
    <div class="field full">
      <label>{{ $t('addr.no') }}</label>
      <input class="id-input" :value="addressNo" :placeholder="$t('addr.noPlaceholder')"
             @input="$emit('update:addressNo', $event.target.value)">
    </div>

    <div class="field full taf-search">
      <label>{{ $t('addr.search') }}</label>
      <input class="id-input" v-model="query" :placeholder="$t('addr.searchPlaceholder')"
             autocomplete="off"
             @focus="onFocus" @input="onInput"
             @keydown.down.prevent="move(1)" @keydown.up.prevent="move(-1)"
             @keydown.enter.prevent="results[hi] && pick(results[hi])"
             @keydown.esc="open = false" @blur="onBlur">
      <button v-if="hasValue" type="button" class="taf-clear" v-hint="$t('addr.clear')"
              @mousedown.prevent="clearSelection">
        <span class="mdi mdi-close-circle"></span>
      </button>
      <div v-if="open" class="taf-pop">
        <div v-if="loading" class="taf-empty">{{ $t('addr.loading') }}</div>
        <template v-else>
          <button v-for="(r, i) in results" :key="r.p + r.a + r.t + r.z" type="button"
                  class="taf-item" :class="{ on: i === hi }"
                  @mousedown.prevent="pick(r)" @mousemove="hi = i">
            <span class="mdi mdi-map-marker-outline"></span>
            <span>{{ formatEntry(r) }}</span>
          </button>
          <div v-if="!results.length" class="taf-empty">
            {{ query.trim() ? $t('addr.noResult') : $t('addr.searchPlaceholder') }}
          </div>
        </template>
      </div>
    </div>

    <div class="field">
      <label>{{ $t('addr.subdistrict') }}</label>
      <input class="id-input taf-ro" :value="subdistrict" readonly tabindex="-1">
    </div>
    <div class="field">
      <label>{{ $t('addr.district') }}</label>
      <input class="id-input taf-ro" :value="district" readonly tabindex="-1">
    </div>
    <div class="field">
      <label>{{ $t('addr.province') }}</label>
      <input class="id-input taf-ro" :value="province" readonly tabindex="-1">
    </div>
    <div class="field">
      <label>{{ $t('addr.postalcode') }}</label>
      <input class="id-input taf-ro" :value="postalcode" readonly tabindex="-1">
    </div>
  </div>
</template>

<script>
// Thai address picker — one type-to-search box fills ตำบล/อำเภอ/จังหวัด/รหัสไปรษณีย์
// with STANDARD values from the bundled dataset, so the stored data is analyzable
// (no more กทม/กรุงเทพ/กรุงเทพฯ spelling drift). Only the street part is free text.
//
// The dataset (~7,450 rows) is lazy-loaded as its own webpack chunk on first focus,
// so it costs nothing on pages that never touch the address fields.
let DATASET = null;
let DATASET_PROMISE = null;
function loadDataset() {
  if (DATASET) return Promise.resolve(DATASET);
  if (!DATASET_PROMISE) {
    DATASET_PROMISE = import(/* webpackChunkName: "thai-address" */ '@/assets/thai-address.json')
      .then((mod) => {
        // shape: { "จังหวัด": { "อำเภอ/เขต": [["ตำบล/แขวง", "zip"], ...] } }
        const grouped = mod.default || mod;
        const rows = [];
        for (const p of Object.keys(grouped)) {
          for (const a of Object.keys(grouped[p])) {
            for (const [t, z] of grouped[p][a]) {
              rows.push({ t, a, p, z, s: `${t} ${a} ${p} ${z}`.toLowerCase() });
            }
          }
        }
        DATASET = rows;
        return rows;
      });
  }
  return DATASET_PROMISE;
}

export default {
  name: 'ThaiAddressFields',
  props: {
    addressNo: { type: String, default: '' },
    subdistrict: { type: String, default: '' },
    district: { type: String, default: '' },
    province: { type: String, default: '' },
    postalcode: { type: String, default: '' },
  },
  emits: ['update:addressNo', 'update:subdistrict', 'update:district', 'update:province', 'update:postalcode'],
  data() {
    return {
      query: '',
      open: false,
      loading: false,
      hi: 0,
      rows: DATASET || [],
    };
  },
  computed: {
    hasValue() {
      return !!(this.subdistrict || this.district || this.province || this.postalcode);
    },
    results() {
      const q = this.query.trim().toLowerCase();
      if (!q || this.rows.length === 0) return [];
      const tokens = q.split(/\s+/);
      const out = [];
      for (const r of this.rows) {
        if (tokens.every((tk) => (/^\d+$/.test(tk) ? r.z.startsWith(tk) : r.s.includes(tk)))) {
          out.push(r);
          if (out.length >= 60) break;
        }
      }
      // entries whose ตำบล starts with the query first — they read as the closest match
      const first = tokens[0];
      out.sort((x, y) => (y.t.toLowerCase().startsWith(first) ? 1 : 0) - (x.t.toLowerCase().startsWith(first) ? 1 : 0));
      return out.slice(0, 20);
    },
  },
  mounted() {
    // editing an existing profile → show the stored selection in the search box
    if (this.hasValue) this.query = this.currentLabel();
  },
  watch: {
    province() { this.syncQuery(); },
    subdistrict() { this.syncQuery(); },
  },
  methods: {
    currentLabel() {
      return this.formatEntry({
        t: this.subdistrict, a: this.district, p: this.province, z: this.postalcode,
      });
    },
    syncQuery() {
      // parent finished loading its data after we mounted → refresh the display text
      if (this.hasValue && !this.open) this.query = this.currentLabel();
    },
    formatEntry(r) {
      const bkk = r.p === 'กรุงเทพมหานคร';
      const segs = [];
      if (r.t) segs.push((bkk ? 'แขวง' : 'ต.') + r.t);
      if (r.a) segs.push(bkk || String(r.a).startsWith('เขต') ? r.a : 'อ.' + r.a);
      if (r.p) segs.push(bkk ? r.p : 'จ.' + r.p);
      if (r.z) segs.push(r.z);
      return segs.join(' ');
    },
    onFocus() {
      this.open = true;
      if (this.rows.length === 0) {
        this.loading = true;
        loadDataset().then((rows) => { this.rows = rows; }).finally(() => { this.loading = false; });
      }
      // selecting again → start a fresh search instead of editing the label text
      if (this.hasValue && this.query === this.currentLabel()) this.query = '';
    },
    onInput() {
      this.open = true;
      this.hi = 0;
    },
    onBlur() {
      this.open = false;
      // nothing picked → put the stored selection (if any) back in the box
      if (this.hasValue) this.query = this.currentLabel();
      else if (!this.query.trim()) this.query = '';
    },
    move(dir) {
      if (!this.results.length) return;
      this.hi = (this.hi + dir + this.results.length) % this.results.length;
    },
    pick(r) {
      this.$emit('update:subdistrict', r.t);
      this.$emit('update:district', r.a);
      this.$emit('update:province', r.p);
      this.$emit('update:postalcode', r.z);
      this.query = this.formatEntry(r);
      this.open = false;
    },
    clearSelection() {
      this.$emit('update:subdistrict', '');
      this.$emit('update:district', '');
      this.$emit('update:province', '');
      this.$emit('update:postalcode', '');
      this.query = '';
      this.open = false;
    },
  },
};
</script>

<style scoped>
.taf { display: contents; }

.taf-search { position: relative; }

.taf-clear {
  position: absolute;
  right: 10px;
  bottom: 9px;
  border: 0;
  background: none;
  padding: 0;
  cursor: pointer;
  color: var(--c-text-muted);
  font-size: 18px;
  line-height: 1;
}
.taf-clear:hover { color: var(--c-error); }

.taf-pop {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 4px;
  z-index: 30; /* above cards (10); these are page forms, not modals */
  background: var(--c-surface);
  border: 1px solid var(--c-border, rgba(0, 0, 0, 0.08));
  border-radius: var(--radius-md);
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.14);
  max-height: 260px;
  overflow-y: auto;
  padding: 4px;
}

.taf-item {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  border: 0;
  background: none;
  text-align: left;
  padding: 8px 10px;
  border-radius: var(--radius-sm, 8px);
  cursor: pointer;
  font-family: var(--font-body);
  font-size: 13.5px;
  color: var(--c-text, inherit);
}
.taf-item .mdi { color: var(--c-primary); font-size: 16px; flex: 0 0 auto; }
.taf-item.on, .taf-item:hover { background: var(--c-primary-soft, rgba(233, 30, 99, 0.08)); }

.taf-empty {
  padding: 12px 10px;
  font-size: 12.5px;
  color: var(--c-text-muted);
}

.taf-ro { opacity: 0.85; pointer-events: none; }
</style>
